import type { FilterCombinator, FilterGroup, FilterNode, FilterRule } from '@/types/filters';
import { MULTI_VALUE_OPERATORS, VALUELESS_OPERATORS } from '@/types/filters';

/**
 * Pure re-implementation of the filter-tree machinery in
 * `@/shared/filters/filter-tree`. That shared module keeps a module-level mutable
 * counter (`nextId`) to mint node ids; forbidden inside a Redux reducer.
 *
 * Here the id counter is an explicit, serialisable `nodeSeq` threaded through
 * every operation, so each transition is deterministic and replayable. The tree
 * and the counter travel together as {@link FilterTreeState}; every function
 * takes a state and returns the next one (or a plain read for the query helpers).
 *
 * This is a deliberate ~duplication (see REFACTOR-FULL-REDUX.md): AddList owns
 * its data layer and must not import the shared hook. Behaviour is kept in parity
 * with `@/shared/filters/filter-tree`; only the id source differs.
 */
export interface FilterTreeState {
  rootGroup: FilterGroup;
  nodeSeq: number;
}

const idFor = (seq: number): string => `fn-${seq}`;

// ── Constructors (pure; take/return the seq) ──

function makeRule(seq: number): { node: FilterRule; nodeSeq: number } {
  const next = seq + 1;
  return {
    node: {
      id: idFor(next),
      kind: 'rule',
      combinator: 'and',
      field: '',
      operator: 'contains',
      value: '',
    },
    nodeSeq: next,
  };
}

function makeGroup(
  seq: number,
  combinator: FilterCombinator = 'and',
): { node: FilterGroup; nodeSeq: number } {
  const next = seq + 1;
  return {
    node: { id: idFor(next), kind: 'group', combinator, children: [] },
    nodeSeq: next,
  };
}

/** Fresh, empty tree (root group + zero children). Mirrors `createEmptyGroup('and')`. */
export function createInitialTree(): FilterTreeState {
  const { node, nodeSeq } = makeGroup(0, 'and');
  return { rootGroup: node, nodeSeq };
}

// ── Internal tree walkers (pure) ──

function addNodeToParent(
  children: FilterNode[],
  parentId: string,
  newNode: FilterNode,
): FilterNode[] {
  return children.map((child) => {
    if (child.id === parentId && child.kind === 'group') {
      return { ...child, children: [...child.children, newNode] };
    }
    if (child.kind === 'group') {
      return { ...child, children: addNodeToParent(child.children, parentId, newNode) };
    }
    return child;
  });
}

function removeNodeFromTree(children: FilterNode[], nodeId: string): FilterNode[] {
  return children
    .filter((child) => child.id !== nodeId)
    .map((child) => {
      if (child.kind === 'group') {
        return { ...child, children: removeNodeFromTree(child.children, nodeId) };
      }
      return child;
    });
}

function updateNodeInTree(
  children: FilterNode[],
  nodeId: string,
  updater: (node: FilterNode) => FilterNode,
): FilterNode[] {
  return children.map((child) => {
    if (child.id === nodeId) return updater(child);
    if (child.kind === 'group') {
      return { ...child, children: updateNodeInTree(child.children, nodeId, updater) };
    }
    return child;
  });
}

function insertNodeAt(
  children: FilterNode[],
  parentId: string,
  newNode: FilterNode,
  index: number,
  rootId: string,
): FilterNode[] {
  if (parentId === rootId) {
    const copy = [...children];
    copy.splice(index, 0, newNode);
    return copy;
  }
  return children.map((child) => {
    if (child.id === parentId && child.kind === 'group') {
      const copy = [...child.children];
      copy.splice(index, 0, newNode);
      return { ...child, children: copy };
    }
    if (child.kind === 'group') {
      return { ...child, children: insertNodeAt(child.children, parentId, newNode, index, rootId) };
    }
    return child;
  });
}

// ── Query helpers ──

/** Depth-first lookup by id, including the root group itself. */
export function findNode(rootGroup: FilterGroup, nodeId: string): FilterNode | null {
  if (rootGroup.id === nodeId) return rootGroup;
  const walk = (children: FilterNode[]): FilterNode | null => {
    for (const child of children) {
      if (child.id === nodeId) return child;
      if (child.kind === 'group') {
        const found = walk(child.children);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(rootGroup.children);
}

/** A rule counts as "active" once it carries enough input to filter the server. */
export function isRuleActive(r: FilterRule): boolean {
  if (!r.field || !r.operator) return false;
  if (VALUELESS_OPERATORS.includes(r.operator)) return true;
  if (MULTI_VALUE_OPERATORS.includes(r.operator)) return (r.values?.length ?? 0) > 0;
  return r.value.trim() !== '';
}

/** Count active rules across the whole tree (feeds the filter-bar badge). */
export function countActiveRules(group: FilterGroup): number {
  return group.children.reduce((sum, child) => {
    if (child.kind === 'rule') return sum + (isRuleActive(child) ? 1 : 0);
    return sum + countActiveRules(child);
  }, 0);
}

// ── Deterministic re-id (seq-threaded rehydrate) ──

/**
 * Re-mint every id in an incoming tree from `nodeSeq`, preserving structure,
 * combinators and rule values. Used when loading a saved/NL-search-derived
 * group so its ids can never collide with this tree's counter.
 */
function reassignIds(group: FilterGroup, seq: number): { group: FilterGroup; nodeSeq: number } {
  let cursor = seq + 1;
  const rootId = idFor(cursor);

  const mapChildren = (children: FilterNode[]): FilterNode[] =>
    children.map((child) => {
      cursor += 1;
      const id = idFor(cursor);
      if (child.kind === 'group') {
        return { ...child, id, children: mapChildren(child.children) };
      }
      return { ...child, id };
    });

  const children = mapChildren(group.children);
  return {
    group: { id: rootId, kind: 'group', combinator: group.combinator, children },
    nodeSeq: cursor,
  };
}

// ── Transitions (state → state) ──

export function addRule(state: FilterTreeState, parentId?: string): FilterTreeState {
  const { node: rule, nodeSeq } = makeRule(state.nodeSeq);
  const { rootGroup } = state;
  if (!parentId || parentId === rootGroup.id) {
    return { rootGroup: { ...rootGroup, children: [...rootGroup.children, rule] }, nodeSeq };
  }
  return {
    rootGroup: { ...rootGroup, children: addNodeToParent(rootGroup.children, parentId, rule) },
    nodeSeq,
  };
}

export function addGroup(
  state: FilterTreeState,
  parentId?: string,
  combinator: FilterCombinator = 'and',
): FilterTreeState {
  // Mirror the hook: create the group, then seed it with one empty rule.
  const group = makeGroup(state.nodeSeq, combinator);
  const rule = makeRule(group.nodeSeq);
  const newGroup: FilterGroup = { ...group.node, children: [rule.node] };
  const nodeSeq = rule.nodeSeq;
  const { rootGroup } = state;
  if (!parentId || parentId === rootGroup.id) {
    return { rootGroup: { ...rootGroup, children: [...rootGroup.children, newGroup] }, nodeSeq };
  }
  return {
    rootGroup: { ...rootGroup, children: addNodeToParent(rootGroup.children, parentId, newGroup) },
    nodeSeq,
  };
}

export function removeNode(state: FilterTreeState, nodeId: string): FilterTreeState {
  return {
    ...state,
    rootGroup: { ...state.rootGroup, children: removeNodeFromTree(state.rootGroup.children, nodeId) },
  };
}

export function updateRule(
  state: FilterTreeState,
  ruleId: string,
  patch: Partial<Omit<FilterRule, 'id' | 'kind'>>,
): FilterTreeState {
  return {
    ...state,
    rootGroup: {
      ...state.rootGroup,
      children: updateNodeInTree(state.rootGroup.children, ruleId, (node) => ({ ...node, ...patch })),
    },
  };
}

export function setCombinator(
  state: FilterTreeState,
  groupId: string,
  combinator: FilterCombinator,
): FilterTreeState {
  const { rootGroup } = state;
  if (groupId === rootGroup.id) {
    return { ...state, rootGroup: { ...rootGroup, combinator } };
  }
  return {
    ...state,
    rootGroup: {
      ...rootGroup,
      children: updateNodeInTree(rootGroup.children, groupId, (node) => ({ ...node, combinator })),
    },
  };
}

export function moveNode(
  state: FilterTreeState,
  nodeId: string,
  targetParentId: string,
  targetIndex: number,
): FilterTreeState {
  const node = findNode(state.rootGroup, nodeId);
  if (!node || nodeId === state.rootGroup.id) return state;

  const withoutNode = removeNodeFromTree(state.rootGroup.children, nodeId);
  if (targetParentId === state.rootGroup.id) {
    const copy = [...withoutNode];
    copy.splice(targetIndex, 0, node);
    return { ...state, rootGroup: { ...state.rootGroup, children: copy } };
  }
  return {
    ...state,
    rootGroup: {
      ...state.rootGroup,
      children: insertNodeAt(withoutNode, targetParentId, node, targetIndex, state.rootGroup.id),
    },
  };
}

/** Reset to a fresh empty tree, minting a new root id (deterministic). */
export function clearTree(state: FilterTreeState): FilterTreeState {
  const { node, nodeSeq } = makeGroup(state.nodeSeq, 'and');
  return { rootGroup: node, nodeSeq };
}

/** Load an external group, re-mint all its ids from `nodeSeq`. */
export function loadTree(state: FilterTreeState, group: FilterGroup): FilterTreeState {
  const { group: rootGroup, nodeSeq } = reassignIds(group, state.nodeSeq);
  return { rootGroup, nodeSeq };
}
